import { h, createContext } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import CartService, { CART_EVENTS } from './cart-service';

const Context = createContext();

const CartContext = ({ children, data }) => {
  const [cartState, setCartState] = useState(CartService.cart);
  const [loading, setLoading] = useState(CartService.loading);

  useEffect(() => {
    const listener = (event) => {
      setLoading(CartService.loading);
      setCartState(CartService.cart);
      if (event === CART_EVENTS.UPDATE) {
        document.dispatchEvent(new CustomEvent('cart:open'));
        document.dispatchEvent(new CustomEvent('ltk:cart:updated'));
      }
    }
    CartService.addListener(listener);
    setCartState(CartService.cart);
    return () => {
      CartService.removeListener(listener);
    }
  }, [])

  useEffect(() => {
    const cartCountEl = document.querySelector('[data-cart-count]');
      if (!cartCountEl) return; 
    
    if (!cartState?.items) {
      cartCountEl.textContent = '';
      cartCountEl.setAttribute('aria-live', 'polite');
      cartCountEl.setAttribute('aria-atomic', 'true');
      
      const btn = cartCountEl.closest('[data-header-cart-trigger]');
      if (btn) btn.setAttribute('aria-label', 'Shopping bag, empty'); 
      return;
    }

    let itemCount = cartState.item_count || 0;
    
    /**
     * If hideEngravingProduct is enabled, subtract engraving products count
     */
    if (data?.hideEngravingProduct) {
      const engravingItemsCount = cartState.items.reduce(
        (count, line) =>
          count +
          ((line.properties?._EngravingProduct || 
            line.properties?._EmbossingProduct) ? line.quantity : 0),
        0
      );
      
      itemCount = Math.max(0, itemCount - engravingItemsCount);
    }
    
    cartCountEl.textContent = itemCount.toString();
    const noun = itemCount === 1 ? 'item' : 'items';
    cartCountEl.setAttribute('aria-live', 'polite');
    cartCountEl.setAttribute('aria-atomic', 'true');

    const btn = cartCountEl.closest('[data-header-cart-trigger]');
    if (btn) {
    btn.setAttribute(
      'aria-label',
      itemCount > 0 ? `Shopping bag, ${itemCount} ${noun}` : 'Shopping bag, empty'
    );
  }
}, [cartState, data?.hideEngravingProduct])

  /**
   * Wrapper for cart API to make interactions more simple
   */
  const cart = {
    add: (...params) => CartService.add(...params),
    remove: (...params) => CartService.remove(...params),
    update: (...params) => CartService.update(...params),
    updateAttributes: (...params) => CartService.updateAttributes(...params),
    state: cartState,
  };

  return (
    <Context.Provider value={{ cart, cartLoading: loading, data }}>
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
(
  <Context.Consumer>
    {(value) => <Rendered {...props} {...value} />}
  </Context.Consumer>
);

export default CartContext;
